import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { withPublicUrl } from '../utils/publicPath';

interface LoadingScreenProps {
  onLoadingComplete: () => void;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ onLoadingComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 991.98);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const preloadAssets = async () => {
      // Only preload the logo for mobile devices
      const assets = isMobile 
        ? [withPublicUrl('/images/logos/splashscreen.png')]
        : [withPublicUrl('/videos/231.webm'), withPublicUrl('/images/logos/splashscreen.png')];

      let loadedCount = 0;

      const loadAsset = (src: string): Promise<void> => {
        return new Promise((resolve) => {
          if (src.endsWith('.webm')) {
            const video = document.createElement('video');
            video.src = src;
            video.oncanplaythrough = () => {
              loadedCount++;
              setProgress((loadedCount / assets.length) * 100);
              resolve();
            };
            video.onerror = () => {
              console.warn(`Failed to load video: ${src}`);
              loadedCount++;
              setProgress((loadedCount / assets.length) * 100);
              resolve();
            };
          } else {
            const img = new Image();
            img.src = src;
            img.onload = () => {
              loadedCount++;
              setProgress((loadedCount / assets.length) * 100);
              resolve();
            };
            img.onerror = () => {
              console.warn(`Failed to load image: ${src}`);
              loadedCount++;
              setProgress((loadedCount / assets.length) * 100);
              resolve();
            };
          }
        });
      };

      try {
        await Promise.all(assets.map(loadAsset));
        // Add a small delay to ensure smooth transition
        setTimeout(() => {
          setIsLoading(false);
          onLoadingComplete();
        }, 500);
      } catch (error) {
        console.warn('Error during asset preloading:', error);
        setIsLoading(false);
        onLoadingComplete();
      }
    };

    preloadAssets();
  }, [onLoadingComplete, isMobile]);

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="loading-screen"
        >
          <div className="loading-content">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="loading-icon"
            >
              <img 
                src={withPublicUrl('/images/logos/splashscreen.png')} 
                alt="CarTech Logo" 
                className="loading-logo"
                onError={(e) => {
                  console.warn('Failed to load loading screen logo');
                }}
              />
            </motion.div>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
              className="loading-progress-bar"
            />
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="loading-text"
            >
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen; 